import React, { useState, useEffect, useRef } from 'react';
import style from './PharmacyFind.module.css';
import HeadSub from '../../../../components/comm/HeadSub';
import URL, { ERMCT_API_KEY } from '../../../../constants/url';
import { Link, useNavigate } from 'react-router-dom';
import iconLocation from '../../../../images/user/i_location_lg.svg';
import PharmacyDetailsBox from '../../../../components/box/PharmacyDetailsBox';
import PharmacyMap from '../../../../components/box/PharmacyMap';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';

import Skeleton from '@mui/material/Skeleton';

function parseHHmmToMinutes(timeStr) {
    if (!timeStr) return null;
    const t = String(timeStr).replace(/\D/g, '');
    if (t.length < 3) return null;
    const hh = parseInt(t.slice(0, t.length - 2), 10);
    const mm = parseInt(t.slice(-2), 10);
    return hh * 60 + mm;
}

/**
 * 화면에 표시할 "HH:MM" 포맷 반환.
 * 24 이상인 시(hour)는 24를 빼서 다음날 시간으로 표시합니다 (예: 2500 -> 01:00).
 */
function formatTime(timeStr) {
    if (!timeStr) return ' - ';
    let t = String(timeStr).replace(/\D/g, '');
    if (t.length < 3) t = t.padStart(4, '0');
    let hh = parseInt(t.slice(0, t.length - 2), 10);
    const mm = t.slice(-2);
    if (hh >= 24) {
        hh = hh - 24; // 다음날 시간으로 표기
    }
    const hhStr = String(hh).padStart(2, '0');
    return `${hhStr}:${mm}`;
}

/**
 * 현재 시간이 start~end 사이인지 체크 (true/false).
 * - start/end 는 "0900" 같은 원본 문자열로 전달.
 * - end <= start 이면 다음날로 넘어가는 구간으로 간주 (ex. 18:00 ~ 02:00)
 * - end 값이 2500 같이 24시 초과이면 정상 처리 (25:00 -> 01:00 다음날)
 */
function isNowBetween(startStr, endStr) {
    const startMin = parseHHmmToMinutes(startStr);
    const endMin = parseHHmmToMinutes(endStr);
    if (startMin == null || endMin == null) return false;

    let s = startMin;
    let e = endMin;

    // end <= start 이면 다음날로 넘어감 -> end에 하루(1440분) 더함
    if (e <= s) e += 1440;

    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();

    // 현재 시간을 확장된 선형(현재, 현재+1440)에서 체크
    return (nowMin >= s && nowMin <= e) || (nowMin + 1440 >= s && nowMin + 1440 <= e);
}

const PharmacyFind = () => {

    const [subType, setSubType] = useState('listView');
    const [open, setOpen] = useState(false);
    const [locationText, setLocationText] = useState('위치 확인중...');
    const [currentPosition, setCurrentPosition] = useState(null);
    const [nearbyPharmacies, setNearbyPharmacies] = useState([]);
    const [selectedPharmacy, setSelectedPharmacy] = useState(null);
    const [loading, setLoading] = useState(true); // 로딩 상태 추가



    // 현재 위치 가져오기
    // useEffect(() => {
    //     const waitForNaver = () => {
    //         if (window.naver && window.naver.maps && window.naver.maps.Service) getAddress();
    //         else setTimeout(waitForNaver, 200);
    //     };

    //     const getAddress = () => {
    //         if (navigator.geolocation) {
    //             navigator.geolocation.getCurrentPosition(
    //                 (pos) => {
    //                     const lat = pos.coords.latitude;
    //                     const lng = pos.coords.longitude;
    //                     setCurrentPosition({ lat, lng });

    //                     const coord = new window.naver.maps.LatLng(lat, lng);
    //                     window.naver.maps.Service.reverseGeocode({ location: coord }, (status, res) => {
    //                         if (status === window.naver.maps.Service.Status.OK) {
    //                             const address = res.result.items[0]?.address || '주소 없음';
    //                             setLocationText(address);
    //                         } else setLocationText('주소 변환 실패');
    //                     });
    //                 },
    //                 (err) => setLocationText('위치 접근 불가')
    //             );
    //         } else setLocationText('위치 서비스 미지원');
    //     };

    //     waitForNaver();
    // }, []);




    // 마커 클릭 핸들러
    const handleMarkerClick = (pharmacy) => {
        setSelectedPharmacy(pharmacy);
        setOpen(true);
    };

    const handleListClick = (pharmacy) => {
        setSelectedPharmacy(pharmacy);
        setOpen(true);
    };



    // 현재 위치가 세팅되면 API 호출
    useEffect(() => {
        if (!currentPosition) return;
        console.log('currentPosition:', currentPosition);

        const fetchPharmacies = async () => {
            try {
                setLoading(true); // 로딩 시작
                const url = `https://apis.data.go.kr/B552657/ErmctInsttInfoInqireService/getParmacyLcinfoInqire?serviceKey=${ERMCT_API_KEY}&WGS84_LON=${currentPosition.lng}&WGS84_LAT=${currentPosition.lat}&numOfRows=50`;

                const response = await fetch(url);
                const text = await response.text(); // 공공데이터포털은 XML 응답이 기본
                const parser = new DOMParser();
                const xml = parser.parseFromString(text, 'application/xml');

                const items = Array.from(xml.getElementsByTagName('item')).map(
                    (el, idx) => {
                        const startRaw =
                            el.getElementsByTagName('startTime')[0]
                                ?.textContent || '';
                        const endRaw =
                            el.getElementsByTagName('endTime')[0]
                                ?.textContent || '';
                        const hours =
                            startRaw || endRaw
                                ? `${formatTime(startRaw)} ~ ${formatTime(
                                    endRaw
                                )}`
                                : '영업시간 정보 없음';
                        const openNow = isNowBetween(startRaw, endRaw);

                        return {
                            id: idx,
                            name:
                                el.getElementsByTagName('dutyName')[0]
                                    ?.textContent || '이름 없음',
                            phone:
                                el.getElementsByTagName('dutyTel1')[0]
                                    ?.textContent || '전화번호 없음',
                            address:
                                el.getElementsByTagName('dutyAddr')[0]
                                    ?.textContent || '주소 없음',
                            startRaw,
                            endRaw,
                            hours,
                            isOpen: openNow,
                            lat: parseFloat(
                                el.getElementsByTagName('latitude')[0]
                                    ?.textContent || 0
                            ),
                            lng: parseFloat(
                                el.getElementsByTagName('longitude')[0]
                                    ?.textContent || 0
                            ),
                        };
                    }
                );

                setNearbyPharmacies(items);
            } catch (error) {
                console.error('API 호출 오류:', error);
                setNearbyPharmacies([]); // 오류시 빈 배열
            } finally {
                setLoading(false); // 로딩 종료
            }
        };

        fetchPharmacies();
    }, [currentPosition]);


    // PharmacyFind.js의 기존 useEffect를 이것으로 교체하세요
    useEffect(() => {
        // RN에서 위치 정보 받는 리스너
        const handleMessage = (event) => {
            console.log('받은 이벤트:', event); // 디버깅용
            console.log('이벤트 데이터:', event.data); // 디버깅용

            try {
                let message;

                // event.data가 이미 객체인 경우와 문자열인 경우 모두 처리
                if (typeof event.data === 'string') {
                    message = JSON.parse(event.data);
                } else if (typeof event.data === 'object' && event.data !== null) {
                    message = event.data;
                } else {
                    console.log('알 수 없는 데이터 형식:', typeof event.data);
                    return;
                }

                console.log('파싱된 메시지:', message); // 디버깅용
                // alert(`웹에서 받은 메시지: ${JSON.stringify(message)}`);

                if (message.type === 'LOCATION_DATA') {
                    const { lat, lng } = message.data;
                    console.log('RN에서 위치 정보 받음:', lat, lng);

                    setCurrentPosition({ lat, lng });

                    // 네이버 지도로 주소 변환
                    const waitForNaver = () => {
                        console.log('네이버 API 체크:', {
                            naver: !!window.naver,
                            maps: !!window.naver?.maps,
                            Service: !!window.naver?.maps?.Service
                        });

                        if (window.naver && window.naver.maps && window.naver.maps.Service) {
                            console.log('네이버 API 준비 완료');
                            const coord = new window.naver.maps.LatLng(lat, lng);
                            window.naver.maps.Service.reverseGeocode({ location: coord }, (status, res) => {
                                console.log('reverseGeocode 응답:', { status, res });
                                if (status === window.naver.maps.Service.Status.OK) {
                                    const address = res.result.items[0]?.address || '주소 없음';
                                    setLocationText(address);
                                } else {
                                    setLocationText('주소 변환 실패');
                                }
                            });
                        } else {
                            console.log('네이버 API 로딩 대기 중...');
                            setTimeout(waitForNaver, 200);
                        }
                    };
                    waitForNaver();
                }

                if (message.type === 'LOCATION_ERROR') {
                    console.log('위치 정보 오류:', message.data.message);
                    setLocationText('위치 접근 불가');
                    alert('위치 정보 오류: ' + message.data.message);
                }
            } catch (error) {
                console.error('메시지 파싱 오류:', error);
                alert('메시지 파싱 오류: ' + error.message);
            }
        };

        // 여러 방법으로 메시지 리스너 등록
        console.log('메시지 리스너 등록 시작');

        // 1. window 이벤트 리스너
        window.addEventListener('message', handleMessage);

        // 2. document 이벤트 리스너 (백업)
        document.addEventListener('message', handleMessage);

        // 3. React Native WebView 전용 리스너 등록 확인
        if (window.ReactNativeWebView) {
            console.log('ReactNativeWebView 감지됨');
            // 추가적인 설정이 필요한 경우 여기에 추가
        }

        console.log('메시지 리스너 등록 완료');

        return () => {
            console.log('메시지 리스너 제거');
            window.removeEventListener('message', handleMessage);
            document.removeEventListener('message', handleMessage);
        };
    }, []); // 빈 dependency array로 한 번만 실행

    // PharmacyFind.js - 위치 정보 없을 때 주기적으로 요청
    useEffect(() => {
        let locationInterval = null;

        const requestLocation = () => {
            if (window.ReactNativeWebView) {
                console.log('위치 권한 요청');
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'REQUEST_LOCATION_PERMISSION'
                }));
            }
        };

        // 위치 정보가 없을 때만 요청 시작
        if (!currentPosition) {
            // 초기 요청
            requestLocation();

            // 3초마다 재요청
            locationInterval = setInterval(() => {
                console.log('위치 정보 재요청 중...');
                requestLocation();
            }, 3000);
        }

        // cleanup에서 인터벌 제거
        return () => {
            if (locationInterval) {
                console.log('위치 요청 인터벌 중지');
                clearInterval(locationInterval);
            }
        };
    }, [currentPosition]);

    // 현재 영업 중인지 확인 함수
    const isOpenNow = (startStr, endStr) => {
        if (!startStr || !endStr) return false;

        const parseHHmmToMinutes = (timeStr) => {
            const t = String(timeStr).replace(/\D/g, '');
            if (t.length < 3) return null;
            const hh = parseInt(t.slice(0, t.length - 2), 10);
            const mm = parseInt(t.slice(-2), 10);
            return hh * 60 + mm;
        };

        const startMin = parseHHmmToMinutes(startStr);
        const endMin = parseHHmmToMinutes(endStr);
        if (startMin == null || endMin == null) return false;

        let s = startMin;
        let e = endMin;

        // end <= start 이면 다음날로 넘어감
        if (e <= s) e += 1440;

        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();

        return (nowMin >= s && nowMin <= e) || (nowMin + 1440 >= s && nowMin + 1440 <= e);
    };

    const onDismiss = () => {
        setOpen(false);
    };


    return (
        <>
            <HeadSub title="근처 약국 찾기" iconClose />

            {/* 위치 데이터 접근을 허용하지 않았을때 */}
            {/* <div id="sub-content" data-layout="white">     
                <div className='inner-wrap'>
                   <div className={style['mid-box']} data-page='no-data'>
                        <div className='animation-2'>
                            <div className={style['mid']}>
                                <object data={iconLocation}/>
                            </div>
                            <div className={style['botm']}>
                                <strong>위치 데이터 접근을 허용해주세요.</strong>
                                <span>위치 데이터 접근 허용 시에만 <br />
                                        약국 찾기 서비스 이용이 가능합니다.
                                </span>
                                <button className='btn m-t-12' data-btn-type="fill-main" data-btn-size='smd' data-btn-icon='arrow'>
                                 <span>설정 바로가기</span>
                            </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div> */}

            {/* 위치 데이터 접근 허용 했을때 */}
            <div id="sub-content" data-top-margin-acc="0">
                <div
                    className="inner-wrap"
                    data-padding="0"
                    data-flex-dir="column"
                >
                    <div className={style['top-cont-box']}>
                        <div className={style['cuurent-posi']}>
                            <span>{locationText}</span>
                        </div>
                        <Box
                            sx={{ display: 'flex' }}
                            className={style['swich-btn-btn']}
                        >
                            <Box className="mask-box">
                                <Box
                                    className="mask"
                                    style={{
                                        transform: `translateX(${subType === 'listView' ? 0 : 'var(--sp-wid-86)'
                                            })`,
                                    }}
                                />
                                <Button
                                    disableRipple
                                    variant="text"
                                    sx={{
                                        color:
                                            subType === 'listView'
                                                ? '#ffffff'
                                                : 'var(--color-main-gray-10)',
                                    }}
                                    onClick={() => setSubType('listView')}
                                    className={
                                        subType === 'listView'
                                            ? `${style.active}`
                                            : null
                                    }
                                >
                                    목록 보기
                                </Button>
                                <Button
                                    disableRipple
                                    variant="text"
                                    sx={{
                                        color:
                                            subType === 'mapView'
                                                ? '#ffffff'
                                                : 'var(--color-main-gray-10)',
                                    }}
                                    onClick={() => setSubType('mapView')}
                                    className={
                                        subType === 'mapView'
                                            ? `${style.active}`
                                            : null
                                    }
                                >
                                    지도 보기
                                </Button>
                            </Box>
                        </Box>

                        <div className={style['noti-chkbox']}>
                            <p className={style['hit']}>꼭 확인해주세요!</p>
                            <span>
                                약국마다 취급하는 약이 달라 처방 받은 약이 없을
                                수 있어요. <br />
                                반드시 약국에 미리 연락해 조제 가능 여부를
                                확인해보세요.
                            </span>
                        </div>
                    </div>

                    <div className={style['bot-cont-box']}>
                        {subType === 'mapView' && currentPosition ? (
                            <div className={style['list-map-box']}>
                                <PharmacyMap
                                    onMarkerClick={handleMarkerClick}
                                    selectedPharmacy={selectedPharmacy}
                                    currentPosition={currentPosition}
                                    pharmacies={nearbyPharmacies} // 지도에 넘김
                                />
                            </div>
                        ) : subType === 'mapView' ? (
                            <div className={style['list-map-box']}>
                                <div className={style['map-loading']}>
                                    <object data={iconLocation} aria-hidden='true'/>
                                    <p>위치 정보를 불러오는 중입니다...</p>
                                </div>
                            </div>
                        ) : (
                            <div className={style['list-view-box']}>
                                <ul>
                                    {loading 
                                        ? Array.from({ length: 5 }).map((_, idx) => (
                                            <li key={idx}>
                                                <Skeleton variant="text" width="60%" height={24} />
                                                <Skeleton variant="text" width="40%" height={20} />
                                                <Skeleton variant="text" width="80%" height={20} />
                                            </li>
                                        ))
                                        : nearbyPharmacies.map((item) => (
                                            <li key={item.id} onClick={() => handleListClick(item)}>
                                                <span data-state={isOpenNow(item.startRaw, item.endRaw) ? '1' : '2'}></span>
                                                <strong>{item.name}</strong>
                                                <span className={style['phon']}>{item.phone}</span>
                                                <span className={style['address']}>{item.address}</span>
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <BottomSheet
                scrollLocking={false} // 내부 스크롤을 위해 BottomSheet 스크롤 잠금 해제
                expandOnContentDrag={false} // 내용 드래그로 확장 방지
                open={open}
                onDismiss={onDismiss}
                snapPoints={({ minHeight }) => minHeight}
                blocking={true}
            >
                {selectedPharmacy && (
                    <PharmacyDetailsBox
                        pharmacy={selectedPharmacy}
                        onClose={() => setOpen(false)}
                        hospitalCode="012" // 추후 진료 받은 병원 코드 추가 !!!!
                    />
                )}
            </BottomSheet>
        </>
    );
};

export default PharmacyFind;

